import * as React from 'react';
import { FC, ReactElement } from 'react';
import { useTheme } from '../../theme';
import { useTranslation } from 'react-i18next';
import { ClassNameMap, DialogClassKey } from '@mui/material';
import ModalX from '../../BaseComponents/ModalX';
import { OwnStyles } from '../types';
import classNames from 'classnames';
import { ModalXProps } from '../../BaseComponents/ModalX/components/ModalX';
import { BaseButton } from '../../BaseComponents';
import { OwnStyles as ModalXStyles } from '../../BaseComponents/ModalX/types';

export interface ModalConfirmProps extends ModalXProps {
	className?: string;
	classes?: Partial<ClassNameMap<DialogClassKey>> & OwnStyles & ModalXStyles;
	onCancel?: () => void;
	onConfirm?: (event?: React.SyntheticEvent<Element, Event>) => void;
	hideTitle?: boolean;
	hideFooter?: boolean;
	cancelBtn?: ReactElement<Element> | string;
	confirmBtn?: ReactElement<Element> | string;
	customButtons?: ReactElement<Element>;
	buttonsExtra?: ReactElement<Element>;
	isLoading?: boolean;
	isCancelButtonLoading?: boolean;
	disableConfirmBtn?: boolean;
}

const ModalConfirm: FC<ModalConfirmProps> = ({
	className,
	classes,
	title,
	cancelBtn,
	confirmBtn,
	customButtons,
	hideFooter = false,
	hideTitle = false,
	children,
	scroll,
	isOpen,
	onClose,
	onCancel,
	onConfirm,
	ignoreClose,
	disableConfirmBtn,
	buttonsExtra,
	isLoading,
	isCancelButtonLoading,
	...props
}) => {
	const theme = useTheme('ModalConfirm')?.ModalConfirm;
	const { t } = useTranslation('ModalConfirm');

	const hideCancelBtn = !onCancel;
	const hideConfirmBtn = !onConfirm;

	function ConfirmButton() {
		if (confirmBtn && typeof confirmBtn !== 'string') {
			return confirmBtn;
		}

		return (
			<BaseButton
				variant="smallPrimary"
				isLoading={isLoading}
				classes={{
					root: classNames(theme.confirmButton, classes?.confirmButton),
					disabled: theme.disabled
				}}
				onClick={onConfirm}
				disabled={disableConfirmBtn}
			>
				{!confirmBtn ? t('Confirm') : confirmBtn}
			</BaseButton>
		);
	}

	function CancelButton() {
		if (cancelBtn && typeof cancelBtn !== 'string') {
			return cancelBtn;
		}

		return (
			<BaseButton
				variant="smallSecondary"
				disabled={isLoading}
				classes={{
					root: classNames(theme.cancelButton, classes?.cancelButton),
					disabled: theme.disabled
				}}
				onClick={onCancel}
				isLoading={isCancelButtonLoading}
			>
				{!cancelBtn ? t('Cancel') : cancelBtn}
			</BaseButton>
		);
	}

	function Buttons() {
		if (customButtons) {
			return (
				<div className={classNames(theme.buttons, classes?.buttons, theme[props.size])}>{customButtons}</div>
			);
		}

		return (
			<div className={classNames(theme.buttons, classes?.buttons, theme[props.size])}>
				{!hideCancelBtn && <CancelButton />}
				{!hideConfirmBtn && <ConfirmButton />}
			</div>
		);
	}

	function Title() {
		return (
			<div className={classNames(theme.header, classes?.header)}>
				<div className={classNames(theme.title, classes?.title)}>{title}</div>
			</div>
		);
	}

	function Footer() {
		return (
			<>
				<div className={classNames(theme.footer, classes?.footer)}>
					{buttonsExtra && (
						<div className={classNames(theme.buttonsExtra, classes?.buttonsExtra)}>{buttonsExtra}</div>
					)}
					<Buttons />
				</div>
			</>
		);
	}

	return (
		<ModalX
			keepMounted={false}
			isOpen={isOpen}
			onClose={onClose}
			ignoreClose={ignoreClose}
			scroll={scroll}
			classes={{
				...theme,
				...classes,
				paper: classNames(theme.paper, classes?.paper),
				closeButton: classes?.closeButton,
				swipeBlock: classes?.swipeBlock,
				content: classNames(theme.content, classes?.content),
				top_wrapper: classes?.top_wrapper,
				dialog_container: classes?.container
			}}
			title={title && !hideTitle && <Title />}
			footer={!hideFooter && <Footer />}
			{...props}
		>
			{children}
		</ModalX>
	);
};

export default ModalConfirm;
